import type { Tokens } from "../../store";

type QueuedRequest<T = unknown> = {
  execute: (tokens: Tokens) => Promise<{ newTokens: Tokens; result: T }>;
  resolve: (result: T) => void;
  reject: (error: Error) => void;
};

type StatusListener = (isProcessing: boolean) => void;

// Module-level state (shared across all hook instances)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const queue: QueuedRequest<any>[] = [];
let isProcessing = false;
let currentTokens: Tokens = { authenticityToken: "", journeyToken: "" };

// Status change listeners
const statusListeners = new Set<StatusListener>();

/**
 * Subscribe to queue processing status changes.
 * Returns an unsubscribe function.
 */
export const subscribeToStatus = (listener: StatusListener): (() => void) => {
  statusListeners.add(listener);
  // Immediately notify of current status
  listener(isProcessing);
  return () => statusListeners.delete(listener);
};

const notifyStatusChange = (newIsProcessing: boolean) => {
  isProcessing = newIsProcessing;
  statusListeners.forEach(listener => listener(newIsProcessing));
};

/**
 * Initialize queue tokens. Called once at app init.
 */
export const initializeTokens = (tokens: Tokens) => {
  currentTokens = tokens;
};

/**
 * Update queue's internal token state. Called after each successful request.
 */
export const updateCurrentTokens = (tokens: Tokens) => {
  currentTokens = tokens;
};

/**
 * Get the current tokens (for testing/debugging).
 */
export const getCurrentTokens = (): Tokens => currentTokens;

/**
 * Enqueue a request that needs token access.
 * Returns a Promise that resolves with the result of THIS specific request.
 *
 * Promise handling:
 * - Each call to enqueueRequest creates a NEW Promise
 * - That Promise's resolve/reject are stored WITH the queued request
 * - When THAT request completes, THAT Promise resolves with THAT result
 * - Other callers' Promises are unaffected
 */
export const enqueueRequest = <T>(
  execute: (tokens: Tokens) => Promise<{ newTokens: Tokens; result: T }>,
): Promise<T> => {
  // Create a new Promise for THIS caller
  return new Promise<T>((resolve, reject) => {
    // Store the execute function AND this Promise's resolve/reject
    queue.push({
      execute,
      resolve, // Will be called with THIS request's result
      reject, // Will be called if THIS request fails
    });

    void processQueue();
  });
};

const processQueue = async () => {
  if (isProcessing) return;
  notifyStatusChange(true);

  while (queue.length > 0) {
    const request = queue.shift()!;

    try {
      // Execute THIS request with current tokens
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { newTokens, result } = await request.execute(currentTokens);

      // Update tokens for next request in queue
      currentTokens = newTokens;

      // Resolve THIS caller's Promise with THIS result
      // The `result` here is whatever the execute function returned
      request.resolve(result);
    } catch (error) {
      // Reject THIS caller's Promise with THIS error
      request.reject(error instanceof Error ? error : new Error(String(error)));
      // Continue processing - other requests in queue are unaffected
    }
  }

  notifyStatusChange(false);
};

/**
 * Check if the queue is currently processing (for testing).
 */
export const getIsProcessing = (): boolean => isProcessing;

/**
 * Get the current queue length (for testing).
 */
export const getQueueLength = (): number => queue.length;

/**
 * Reset queue state. Used for testing cleanup.
 */
export const resetTokenQueue = () => {
  queue.length = 0;
  isProcessing = false;
  currentTokens = { authenticityToken: "", journeyToken: "" };
  statusListeners.clear();
};
